import React, { useMemo } from "react";

import { partitionBy } from "@examspark/common";
import { useAppContext } from "@examspark/common-app";
import { FormContext } from "@examspark/common-ui";
import { Annotation } from "@examspark/types";

import { CreateAnnotationParams } from "../unit/Annotations";

import AnnotationDetail from "./AnnotationDetail";

type Props = {
  annotations: Annotation[];
  onCreateAnnotation: (params: CreateAnnotationParams) => Promise<void>;
  onUpdateAnnotation: (annotation: Annotation) => Promise<void>;
  onDeleteAnnotation: (annotation: Annotation) => Promise<void>;
  isLoading?: boolean;
};

/**
 * The UI internals for annotation display. This copies content from
 * `AnnotationsModal`. We might be able to remove the guts of `AnnotationsModal`
 * and replace it with this if we desire to use this UI in verious vessels.
 */
const AnnotationsPanel = ({
  annotations,
  onCreateAnnotation,
  onUpdateAnnotation,
  onDeleteAnnotation,
  isLoading,
}: Props) => {
  const [state] = useAppContext();

  const partitionedAnnotations = useMemo(
    () =>
      partitionBy(annotations, (a) =>
        a.sk.match(/workspace/) ? (a.bankId ?? undefined) : "user",
      ),
    [annotations],
  );

  return (
    <FormContext.Provider
      value={{
        disabled:
          isLoading ?? state.appLicenseConfig?.userContentBankId == null,
      }}
    >
      <AnnotationDetail
        placeholder="Save personal notes to this document for reference anytime."
        annotations={partitionedAnnotations["user"]}
        canEdit
        onCreate={async (resource) => {
          return onCreateAnnotation({
            skResourceId: `${state.user?.id}/user`,
            bankId: state.appLicenseConfig?.userContentBankId,
            resource,
          });
        }}
        onUpdate={onUpdateAnnotation}
        onDelete={onDeleteAnnotation}
      />
    </FormContext.Provider>
  );
};

export default AnnotationsPanel;
